import * as THREE from 'three';
import { Reflector } from 'three/examples/jsm/objects/Reflector.js';
import { WebglScene } from '../scene';
import { OBJLoader } from '../obj/OBJLoader';
import { BINLoader } from '../obj/BINLoader';
import { Cleaner } from '../helpers/cleaner';
import { Material } from '../helpers/material';
import { frosted } from '../textures/frosted';
import { WebglHullEntity } from '../hullEntity';
import { holdoutMaterial } from '../../constants';
import { Coating, ComponentConstants, Entity, Floor, Joinery, Room, roomManager, Scene, SceneConstants, Staircase, TechnicalElement, Wall } from '@rhinov/savane-js';

export class StaticHull {

    public hull: THREE.Group;
    public group: THREE.Group;
    public mirrors: Array<{ object: Reflector, entity: Joinery }> = new Array<{ object: Reflector, entity: Joinery }>();
    
    private savane: Scene;
    private scene: WebglScene;
    private _entity: Entity | null;

    constructor(obj: string, savane: Scene, scene: WebglScene) {
        this.savane = savane;
        this.scene = scene;
        let loader;
        if (!scene.settings.hullbin) {
            loader = new OBJLoader();
            this.hull = loader.parse(obj);
        }
        else {
            loader = new BINLoader();
            this.hull = loader.parse(obj).object;
        }
        this.hull.name = "StaticHull";
        this.group = new THREE.Group();
        this.group.add(this.hull);
        this.mirrors = [];
        //updated entity
        this._entity = null;
        this._process();
        this.hideCeiling();
    }

    static create(obj: string, savane: Scene, scene: WebglScene) {
        return new StaticHull(obj, savane, scene);
    }

    cleanChild(child: THREE.Mesh) : void {
        if (child.geometry) {
            child.geometry.dispose();
        }
        if ((child as any).body) {
            this.scene.physics.destroy((child as any).body);
        }
        if (child.material) {
            Cleaner.cleanMaterial(child.material);
        }
        if ((child as any).cubeCamera) {
            (child as any).cubeCamera.renderTarget.dispose();
        }
    }

    dispose() : void {
        let traversing = (child: any) => {
            this.cleanChild(child);
        }
        for (let i = 0; i < this.mirrors.length; ++i) {
            this.mirrors[i].object.getRenderTarget().dispose();
            this.cleanChild(this.mirrors[i].object);
            this.mirrors[i].object.removeFromParent();
        }
        this.mirrors = [];
        this.hull.traverse(traversing);
    }

    update(entity: Entity) : void {
        this._entity = entity;
        this.hull.traverse(this._processChild.bind(this));
        this._entity = null;
    }

    updateCoatingParameters(entity: Entity) : void {
        let traversing = function(this: any, child: any) {
            if (child.name.startsWith("Wall")) {
                let wallParse = child.name.split('_');
                let wallId = Number(wallParse[1]);

                let hangType = Coating.HangType.wallDirect;
                if (wallParse[3] === 'Indirect') {
                    hangType = Coating.HangType.wallUndirect;
                } else if (wallParse[2] === 'Top') {
                    hangType = Coating.HangType.wallTop;
                } else if (wallParse[2] === 'Bottom') {
                        hangType = Coating.HangType.wallBottom;
                } else if (wallParse[2] === 'RightSide') {
                    hangType = Coating.HangType.wallRightSide;
                } else if (wallParse[2] === 'LeftSide') {
                    hangType = Coating.HangType.wallLeftSide;
                }

                if (wallId === entity.id) {
                    let wall = this.savane.getDeepChild(wallId);
                    if (!wall) return;
                    let coatings: any[] = wall.getComponents(ComponentConstants.ComponentType.Coating);

                    for (let i = 0; i < coatings.length; i++) {
                        if (coatings[i].hangType === hangType) {
                            let material = child.material as THREE.MeshPhongMaterial;
                            if (material.map) {
                                material.map.rotation = (coatings[i].rotation * Math.PI) / 180;
                                material.map.offset.x = (coatings[i].offset[0]);
                                material.map.offset.y = (coatings[i].offset[1]);
                                material.map.repeat.x = (material.map as any).originalRepeat.x * (coatings[i].repeat[0]);
                                material.map.repeat.y = (material.map as any).originalRepeat.y * (coatings[i].repeat[1]);
                            }
                        }
                    }
                }
            }
            else if (child.name.indexOf("Ceiling") !== -1) {
                //add node to wall
                let ceilingParse = child.name.split('_');
                let index = 1;
                if (child.name.startsWith("axo")) {
                    index++;
                }
                let roomId = Number(ceilingParse[index]);

                if (roomId === entity.id) {
                    let room = this.savane.getDeepChild(roomId);

                    if (room !== null) {
                        let coatings: any[] = room.getComponents(ComponentConstants.ComponentType.Coating);

                        if (coatings !== null) {
                            for (let i = 0; i < coatings.length; i++) {
                                if (coatings[i].hangType === Coating.HangType.ceiling) {
                                    let material = child.material as THREE.MeshPhongMaterial;
                                    if (material.map) {
                                        material.map.rotation = (coatings[i].rotation * Math.PI) / 180;
                                        material.map.offset.x = (coatings[i].offset[0]);
                                        material.map.offset.y = (coatings[i].offset[1]);
                                        material.map.repeat.x = (material.map as any).originalRepeat.x * (coatings[i].repeat[0]);
                                        material.map.repeat.y = (material.map as any).originalRepeat.y * (coatings[i].repeat[1]);
                                    }
                                }
                            }
                        }
                    }
                }
            }
            else if (child.name.startsWith("axo_Slope")) {
                let slopeParse = child.name.split('_');
                let wallId = Number(slopeParse[2]);

                if (wallId === entity.id) {
                    let directCoating = (slopeParse[3] === 'Direct');
                    let wall = this.savane.getDeepChild(wallId);

                    if (wall !== null) {
                        let coatings: any[] = wall.getComponents(ComponentConstants.ComponentType.Coating);

                        if (coatings !== null) {
                            for (let i = 0; i < coatings.length; i++) {
                                if ((coatings[i].hangType === Coating.HangType.slopeDirect && directCoating) || (coatings[i].hangType === Coating.HangType.slopeUndirect && !directCoating)) {
                                    let material = child.material as THREE.MeshPhongMaterial;
                                    if (material.map) {
                                        material.map.rotation = (coatings[i].rotation * Math.PI) / 180;
                                        material.map.offset.x = (coatings[i].offset[0]);
                                        material.map.offset.y = (coatings[i].offset[1]);
                                        material.map.repeat.x = (material.map as any).originalRepeat.x * (coatings[i].repeat[0]);
                                        material.map.repeat.y = (material.map as any).originalRepeat.y * (coatings[i].repeat[1]);
                                    }
                                    break;
                                }
                            }
                        }
                    }
                }
            }
            else if (child.name.indexOf("Joinery") !== -1) {
                //add node to wall
                let joineryParse = child.name.split('_');

                let joineryId = Number(joineryParse[2]);

                if (joineryId === entity.id) {
                    let joinery = this.savane.getDeepChild(joineryId);

                    if (joinery !== null) {
                        let coatings: any[] = joinery.getComponents(ComponentConstants.ComponentType.Coating);

                        if (coatings !== null) {
                            for (let i = 0; i < coatings.length; i++) {
                                if (Array.isArray(child.material)) {
                                    for (let j = 0 ; j < child.material.length ; j++) {
                                        let material = child.material as Array<THREE.MeshPhongMaterial>;
                                        if (material[j].map) {
                                            material[j].map!.rotation = (coatings[i].rotation * Math.PI) / 180;
                                            material[j].map!.offset.x = (coatings[i].offset[0]);
                                            material[j].map!.offset.y = (coatings[i].offset[1]);
                                            material[j].map!.repeat.x = (material[j].map as any).originalRepeat.x * (coatings[i].repeat[0]);
                                            material[j].map!.repeat.y = (material[j].map as any).originalRepeat.y * (coatings[i].repeat[1]);
                                        }
                                    }
                                }
                                else {
                                    let material = child.material as THREE.MeshPhongMaterial;
                                    if (material.map) {
                                        material.map.rotation = (coatings[i].rotation * Math.PI) / 180;
                                        material.map.offset.x = (coatings[i].offset[0]);
                                        material.map.offset.y = (coatings[i].offset[1]);
                                        material.map.repeat.x = (material.map as any).originalRepeat.x * (coatings[i].repeat[0]);
                                        material.map.repeat.y = (material.map as any).originalRepeat.y * (coatings[i].repeat[1]);
                                    }
                                }
                            }
                        }
                    }
                }
            }
            else if (child.name.indexOf("TechnicalElement") !== -1) {
                let techElementParse = child.name.split('_');
                let index = techElementParse.indexOf("TechnicalElement");

                if (index === -1) {
                    return;
                }

                let id = Number(techElementParse[index + 2]);

                if (id === entity.id) {
                    let techElement = this.savane.getDeepChild(id);
                    if (techElement) {
                        let coatings: any[] = techElement.getComponents(ComponentConstants.ComponentType.Coating);

                        if (coatings !== null) {
                            for (let i = 0; i < coatings.length; i++) {
                                if (Array.isArray(child.material)) {
                                    for (let j = 0 ; j < child.material.length ; j++) {
                                        if (child.material[j].name === coatings[i].usemtlName) {
                                            let material = child.material as Array<THREE.MeshPhongMaterial>;
                                            if (material[j].map) {
                                                material[j].map!.rotation = (coatings[i].rotation * Math.PI) / 180;
                                                material[j].map!.offset.x = (coatings[i].offset[0]);
                                                material[j].map!.offset.y = (coatings[i].offset[1]);
                                                material[j].map!.repeat.x = (material[j].map as any).originalRepeat.x * (coatings[i].repeat[0]);
                                                material[j].map!.repeat.y = (material[j].map as any).originalRepeat.y * (coatings[i].repeat[1]);
                                            }
                                        }
                                    }
                                }
                                else {
                                    if (child.material.name === coatings[i].usemtlName) {
                                        let material = child.material as THREE.MeshPhongMaterial;
                                        if (material.map) {
                                            material.map.rotation = (coatings[i].rotation * Math.PI) / 180;
                                            material.map.offset.x = (coatings[i].offset[0]);
                                            material.map.offset.y = (coatings[i].offset[1]);
                                            material.map.repeat.x = (material.map as any).originalRepeat.x * (coatings[i].repeat[0]);
                                            material.map.repeat.y = (material.map as any).originalRepeat.y * (coatings[i].repeat[1]);
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
            else if (child.name.indexOf("Stair") !== -1) {
                let stairElementParse = child.name.split('_');
                let index = stairElementParse.indexOf("Stair");

                if (index === -1) {
                    index = stairElementParse.indexOf("StairGuardsRail");
                    if (index === -1) {
                        return;
                    }
                }

                let id = Number(stairElementParse[index + 1]);

                if (id === entity.id) {
                    let stairElement = this.savane.getDeepChild(id);
                    if (stairElement) {
                        let coatings: any[] = stairElement.getComponents(ComponentConstants.ComponentType.Coating);

                        if (coatings !== null) {
                            for (let i = 0; i < coatings.length; i++) {
                                if (Array.isArray(child.material)) {
                                    for (let j = 0 ; j < child.material.length ; j++) {
                                        if (child.material[j].name === coatings[i].usemtlName) {
                                            let material = child.material as Array<THREE.MeshPhongMaterial>;
                                            if (material[j].map) {
                                                material[j].map!.rotation = (coatings[i].rotation * Math.PI) / 180;
                                                material[j].map!.offset.x = (coatings[i].offset[0]);
                                                material[j].map!.offset.y = (coatings[i].offset[1]);
                                                material[j].map!.repeat.x = (material[j].map as any).originalRepeat.x * (coatings[i].repeat[0]);
                                                material[j].map!.repeat.y = (material[j].map as any).originalRepeat.y * (coatings[i].repeat[1]);
                                            }
                                        }
                                    }
                                }
                                else {
                                    if (child.material.name === coatings[i].usemtlName) {
                                        let material = child.material as THREE.MeshPhongMaterial;
                                        if (material.map) {
                                            material.map.rotation = (coatings[i].rotation * Math.PI) / 180;
                                            material.map.offset.x = (coatings[i].offset[0]);
                                            material.map.offset.y = (coatings[i].offset[1]);
                                            material.map.repeat.x = (material.map as any).originalRepeat.x * (coatings[i].repeat[0]);
                                            material.map.repeat.y = (material.map as any).originalRepeat.y * (coatings[i].repeat[1]);
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        };

        this.hull.traverse(traversing.bind(this));
    }

    showCeiling() : void {
        let traversing = (child: any) => {
            if (child.name.startsWith("axo")) {
                if (child.name.startsWith("axo_Ceiling") || child.name.startsWith("axo_Slope") ||
                    child.name.startsWith("axo_Stair") || child.name.startsWith("axo_Tremie")) {
                    if (!this.scene.settings.interactiveProject) {
                        child.castShadow = true;
                    }
                    child.receiveShadow = true;
                }

                if (child.name.startsWith("axo_Ceiling")) {
                    let entityNb = Number(child.name.split("_")[2]);

                    if (entityNb < 0) {
                        (child.material as THREE.MeshPhongMaterial).side = THREE.FrontSide;
                    }
                }

                child.layers.set(0);
            }
        };
        this.hull.traverse(traversing);
    }

    hideCeiling() : void {
        let traversing = (child: any) => {
            // Ceiling always hidden
            let hide = false;
            if (child.name.startsWith("axo_TechnicalElement") ||
                child.name.startsWith("axo_Stair") || child.name.startsWith("axo_Tremie")) {
                hide = true;
            }
            if (child.name.startsWith("axo_Ceiling")) {
                let entityNb = Number(child.name.split("_")[2]);

                if (entityNb > 0) {
                    hide = true;
                }
                else  {
                    (child.material as THREE.MeshPhongMaterial).side = THREE.DoubleSide;
                }
            }

            // Hide only if top view camera (this.camera === null) hide slopes and axo_joineries (velux)
            //if (this.camera === null) {
            if (child.name.startsWith("axo_Slope") || child.name.startsWith("axo_Joinery") || child.name.startsWith("axo_Custom")) {
                hide = true;
            }
            //}

            if (hide === true) {
                child.castShadow = false;
                child.receiveShadow = false;
                child.layers.set(1);
            }
        };
        this.hull.traverse(traversing);
    }

    showUpperFloor(floor: Floor) : void {
        if (!floor) return;

        this.hull.traverse((child: THREE.Object3D) => {
            let entity = this.getEntityFromChild(child);
            if (entity && entity.floor && entity.floor.height > floor.height) {
                child.layers.set(0);
            }
        });
    }

    hideUpperFloor(floor: Floor) : void {
        this.hull.traverse((child: THREE.Object3D) => {
            let entity = this.getEntityFromChild(child);
            if (entity && entity.floor && entity.floor.height > floor.height) {
                child.layers.set(1);
            }
        });
    }

    hideWalls(excluded: Array<Wall>) : void {
        let traversing = (child: any) => {
            if (child.name.startsWith("Wall")) {
                let parse = child.name.split('_');
                let id = Number(parse[1]);
                let hide = true;
                for (let i = 0; i < excluded.length; ++i) {
                    if (excluded[i].id === id) {
                        hide = false;
                        break;
                    }
                }
                if (hide) {
                    child.layers.set(1);
                    if (Array.isArray(child.material)) {
                        for (let i = 0; i < child.material.length; ++i) {
                            child.layers.set(1);
                        }
                    } else {
                        child.layers.set(1);
                    }
                }
            }
        };
        this.hull.traverse(traversing);
    }

    showWalls() : void {
        let traversing = (child: any) => {
            if (!child.name.startsWith("Wall")) {
                return;
            }

            child.layers.set(0);
            if (Array.isArray(child.material)) {
                for (let i = 0; i < child.material.length; ++i) {
                    child.layers.set(0);
                }
            } else {
                child.layers.set(0);
            }
        };
        this.hull.traverse(traversing);
    }

    filterFloor(floor: Floor) : void {
        this.hull.traverse((child: THREE.Object3D) => {
            let entity = this.getEntityFromChild(child);
            if (floor !== null) {
                if (entity !== null && floor.getDeepChild(entity.id)) {
                    child.layers.set(0);
                } else {
                    child.layers.set(1);
                }
            } else {
                child.layers.set(0);
            }

        });
    }

    static getIdFromChild(child: THREE.Object3D) : number | null {
        let result = null;

        if (child.name.startsWith("Wall")) {
            let parse = child.name.split('_');
            result = Number(parse[1]);
        } else if (child.name.startsWith("TechnicalElementPlinth")) {
            let parse = child.name.split('_');
            result = Number(parse[2]);
        } else if (child.name.indexOf("TechnicalElement") !== -1) {
            let parse = child.name.split('_');
            let index = parse.indexOf("TechnicalElement");
            result = Number(parse[index + 2]);
        } else if (child.name.startsWith("Floor") && !child.name.startsWith("FloorPlinth")) { // FloorPlinth is part of the FGHull
            let parse = child.name.split('_');
            result = Number(parse[1]);
        } else if (child.name.indexOf("Ceiling") !== -1) {
            let parse = child.name.split('_');
            let index = 1;
            if (child.name.startsWith("axo")) {
                index++;
            }
            result = Number(parse[index]);
        } else if (child.name.startsWith("Joinery") || child.name.startsWith("axo_Joinery")) {
            let parse = child.name.split('_');
            result = (child.name.startsWith("axo") ? Number(parse[3]) : Number(parse[2]));
        } else if (child.name.startsWith("Stair") || child.name.startsWith("axo_Stair")) {
            let parse = child.name.split('_');
            result =  (child.name.startsWith("axo") ? Number(parse[2]) : Number(parse[1]));
        } else if (child.name.startsWith("Plinth")) {
            let parse = child.name.split('_');
            result = Number(parse[1]);
        } else if (child.name.indexOf("axo_Cornice") !== -1) {
            let parse = child.name.split('_');
            result = Number(parse[2]);
        } else if (child.name.indexOf("Slope") !== -1) {
            let parse = child.name.split('_');
            result = Number(parse[2]);
        } else if (child.name.indexOf("DressingElement") !== -1) {
            let parse = child.name.split('_');
            result = Number(parse[2]);
        }

        return result ? Math.abs(result) : null;
    }

    getChildsFromId(id: number) : Array<THREE.Object3D> {
        let result: Array<THREE.Object3D> = [];
        this.hull.traverse((child: THREE.Object3D) => {
            if (id === StaticHull.getIdFromChild(child)) {
                result.push(child);
            }
        });
        return result;
    }

    getEntityFromChild(child: THREE.Object3D) : Entity | null {
        let result: Entity | null = null;
        let id = StaticHull.getIdFromChild(child);
        if (id) {
            result = this.savane.getDeepChild(id);
            if (!result && child.name.indexOf("DressingElement") !== -1) {
                result = this.savane.getDeepChild(id - 1000);
            }
        }
        return result;
    }

    static replaceChildNameId(child: THREE.Object3D, id: number) : void {
        if (child.name.indexOf("TechnicalElement") !== -1) {
            let parse = child.name.split('_');
            let index = parse.indexOf("TechnicalElement");
            parse[index + 2] = String(id);
            child.name = parse.join('_');
        } else if (child.name.startsWith("Stair")) {
            let parse = child.name.split('_');
            parse[1] = String(id);
            child.name = parse.join('_');
        } else if (child.name.startsWith("axo_Stair")) {
            let parse = child.name.split('_');
            parse[2] = String(id);
            child.name = parse.join('_');
        }
    }

    setCustomCoating(object: THREE.Mesh, customCoating: { coating: Coating }) : void {
        Material.setMaterialFromCoating(object, customCoating.coating, true, undefined, this.scene, true, null);
    }

    _process() : void {
        this.hull.traverse(this._processChild.bind(this));
    }

    _createCubeCamera(child: any, resolution: number) : void {
        if (child.cubeCamera) {
            child.cubeCamera.renderTarget.dispose();
        }
        let bbox = new THREE.Box3().setFromObject(child);
        let renderTarget = new THREE.WebGLCubeRenderTarget(resolution, {
            format: THREE.RGBAFormat,
            anisotropy: 2,
            generateMipmaps: true,
            minFilter: THREE.LinearMipmapLinearFilter
        });
        child.cubeCamera = new THREE.CubeCamera(1, 1000, renderTarget);
        bbox.getCenter(child.cubeCamera.position);
    }

    _handleAxo(child: THREE.Mesh, entity: Entity) : void {
        if (!child.name.startsWith("axo")) {
            return;
        }

        if (child.name.indexOf("Joinery") !== - 1) {
            return;
        }

        if (child.name.indexOf("TechnicalElement") !== - 1) {
            return;
        }

        if (child.name.indexOf("Ceiling") !== -1) {
            return;
        }

        if (child.name.startsWith("axo_Slope")) {
            let slopeParse = child.name.split('_');
            let wallId = Number(slopeParse[2]);
            let directCoating = (slopeParse[3] === 'Direct');

            if (entity && entity.id !== wallId) {
                return;
            }

            // We do not put any color on tops and bottoms and on walls not in a room
            let wall = this.savane.getDeepChild(wallId);
            if (!wall) {
                return;
            }

            let coatings = wall.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;
            coatings = coatings.filter(function(item) {
                return item.hangType === Coating.HangType.slopeDirect || item.hangType === Coating.HangType.slopeUndirect;
            })
            if ((coatings !== null) && (coatings.length > 0)) {
                for (let i = 0; i < coatings.length; i++) {
                    if ((child as any).coatingId === coatings[i].coatingId) {
                        continue;
                    }

                    if ((coatings[i].hangType === Coating.HangType.slopeDirect && directCoating) || (coatings[i].hangType === Coating.HangType.slopeUndirect && !directCoating)) {
                        Material.setMaterialFromCoating(child, coatings[i], true, undefined, this.scene, false, null);
                        (child as any).coatingId = coatings[i].coatingId;
                        break;
                    }
                }
            }
            else {
                //Set material
                child.material = Material.default(new THREE.Color(0x505050).convertSRGBToLinear(), null, null);
            }
        } else if (child.name.startsWith("axo_Cornice")) {
            let corniceParse = child.name.split('_');
            let wallId = Number(corniceParse[2]);
            let directCoating = (corniceParse[4] === 'Direct' || corniceParse[5] === 'Direct');
            let roomId = Number(corniceParse[3]);
            let room = this.savane.getDeepChild(roomId) as Room;
            if (!room) {
                return;
            }

            if (entity && entity.id !== wallId) {
                return;
            }

            // We do not put any color on tops and bottoms and on walls not in a room
            let wall = this.savane.getDeepChild(wallId);
            if (!wall) {
                return;
            }

            let coatings = wall.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;
            coatings = coatings.filter(function(item) {
                return item.hangType === Coating.HangType.corniceDirect || item.hangType === Coating.HangType.corniceUndirect;
            })
            if ((coatings !== null) && (coatings.length > 0)) {
                for (let i = 0; i < coatings.length; i++) {
                    if ((child as any).coatingId === coatings[i].coatingId) {
                        continue;
                    }

                    if ((coatings[i].hangType === Coating.HangType.corniceDirect && directCoating) || (coatings[i].hangType === Coating.HangType.corniceUndirect && !directCoating)) {
                        Material.setMaterialFromCoating(child, coatings[i], true, undefined, this.scene, false, null);
                        (child as any).coatingId = coatings[i].coatingId;
                        return;
                    }
                }
            }
            else {
                switch (room.cornicesMaterialType) {
                    // White
                    case 0:
                        child.material = Material.default(new THREE.Color(0xffffff), null, {
        
                        });
                        break;
        
                    // Like room ceiling
                    case 1:
                        let coatings = room.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;
                        let hangType = Coating.HangType.ceiling;
                        let found = false;
                        for (let i = 0; i < coatings.length; i++) {
        
                            if (coatings[i].hangType === hangType) {
                                if (!coatings[i].floorGeneratorSettings) {
                                    Material.setMaterialFromCoating(child, coatings[i], true, undefined, this.scene, false, null);
                                    (child as any).coatingId = coatings[i].coatingId;
                                }
                                if (coatings[i].floorGeneratorSettings && coatings[i].floorGeneratorSettings.jointCoating) {
                                    child.material = Material.default(new THREE.Color(coatings[i].floorGeneratorSettings.jointCoating.colors[0].realColor).convertSRGBToLinear(), null, {
        
                                    });
                                    (child as any).coatingId = coatings[i].floorGeneratorSettings.jointCoating.coatingId;
                                }
                                found = true;
                                break;
                            }
                        }
                        if (found == false) {
                            // Default color assignment when no coating
                            child.material = Material.default(new THREE.Color(0xffffff), null, {
        
                            });
                        }
                        break;
        
                    // Like wall
                    case 2:
                        if (wall !== null) {
                            let coatings = wall.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;
        
                            if ((coatings !== null) && (coatings.length > 0)) {
                                for (let i = 0; i < coatings.length; i++) {
                                    if ((child as any).coatingId === coatings[i].coatingId) {
                                        continue;
                                    }
        
                                    if ((coatings[i].hangType === Coating.HangType.wallDirect && directCoating) || (coatings[i].hangType === Coating.HangType.wallUndirect && !directCoating)) {
                                        Material.setMaterialFromCoating(child, coatings[i], true, undefined, this.scene, false, null);
                                        (child as any).coatingId = coatings[i].coatingId;
                                        break;
                                    }
                                }
                            }
                            else {
                                //Set material
                                child.material = Material.default(new THREE.Color(0xffffff), null, {
        
                                });
                            }
                        }
                        break;
                }
            }
        } else {
            //set axo color to white
            if (Array.isArray(child.material)) {
                let material = child.material as Array<THREE.MeshPhongMaterial>
                for (let i = 0; i < material.length; ++i) {
                    material[i].color = new THREE.Color(0xffffff);
                }
            } else {
                let material = child.material as THREE.MeshPhongMaterial
                material.color = new THREE.Color(0xffffff);
            }
        }
    }

    _createGlass(child: THREE.Mesh, joinery: Joinery) {
        // 8 is glass
        // 7 is special glass with deformation
        if (joinery.frosted) {
            // Joinery with frosted glass
            this._createCubeCamera(child, 1024);
            (child as any).cubeCamera.renderTarget.texture.mapping = THREE.CubeRefractionMapping;
            for (let i = 0; Array.isArray(child.material) && i < child.material.length; ++i) {
                let material = child.material[i] as THREE.MeshPhongMaterial;
                material.envMap = (child as any).cubeCamera.renderTarget.texture;
                if (material.name === "8" || material.name === "7") {
                    material.color = new THREE.Color(1, 1, 1);
                    material.transparent = false;
                    (material as any).alwaysTransparent = true;
                    material.refractionRatio = 1.0 / 1.5;
                    let normalMap = new Image();
                    normalMap.src = frosted;
                    material.normalMap = new THREE.TextureLoader().load(normalMap.src);
                    material.normalMap.wrapS = THREE.RepeatWrapping;
                    material.normalMap.wrapT = THREE.RepeatWrapping;
                    material.normalMap.repeat.set(6, 6);
                    material.normalScale.set(1.5, 1.5);
                }
            }
        }
        else {
            child.geometry.groups = child.geometry.groups.filter((item: any) => {
                if ((child.material as any)[item.materialIndex].name === "8" || (child.material as any)[item.materialIndex].name === "7") {
                    let geometry = new THREE.BufferGeometry();
                    let vertices = child.geometry.getAttribute('position').clone();
                    vertices.array = vertices.array.slice(item.start * vertices.itemSize, (item.start + item.count) * vertices.itemSize);
                    (vertices as any).count = item.count;
                    geometry.setAttribute('position', vertices);

                    let normals = child.geometry.getAttribute('normal').clone();
                    normals.array = normals.array.slice(item.start * normals.itemSize, (item.start + item.count) * normals.itemSize);
                    (normals as any).count = item.count;
                    geometry.setAttribute('normal', normals);

                    (child.material as any)[item.materialIndex].alwaysTransparent = true;
                    let material = Material.clone((child.material as any)[item.materialIndex], this.scene);
                    let mesh = new THREE.Mesh(geometry, material);
                    this._setTransparencyCallback(mesh);
                    mesh.name = child.name;
                    material.color = new THREE.Color(0xffffff);
                    material.transparent = true;
                    material.reflectivity = 0.15;
                    material.opacity = 0.05;
                    (material as any).isGlass = true;
                    (material as any).alwaysTransparent = true;
                    mesh.castShadow = false;
                    (mesh as any).isGlass = true;
                    if (material.name === "7") {
                        this._createCubeCamera(mesh, 1024);
                        material.transparent = false;
                        material.reflectivity = 1;
                        material.refractionRatio = 1.0 / 1.5;
                        material.envMap = (mesh as any).cubeCamera.renderTarget.texture;
                        (mesh as any).cubeCamera.renderTarget.texture.mapping = THREE.CubeRefractionMapping;
                    }

                    child.add(mesh);
                    return false;
                }
                return true;
            });
        }
    }

    _createMirror(child: THREE.Mesh, joinery: Joinery, options: any) : void {
        // 9 is mirror
        // remove mirror geometry from current child

        if (!this.scene.settings.mirrors) {
            return;
        }

        child.geometry.groups = child.geometry.groups.filter((item: any) => {
            if (!options.cuisinella && (child.material as any)[item.materialIndex].name === "9" ||
                ((options.cuisinella && options.mirror) && ((child.material as any)[item.materialIndex].name === "1" || (child.material as any)[item.materialIndex].name === "2"))) { // create a new mirror child
                let geometry = new THREE.BufferGeometry();
                let vertices = child.geometry.getAttribute('position').clone();
                vertices.array = vertices.array.slice(item.start * vertices.itemSize, (item.start + item.count) * vertices.itemSize);
                (vertices as any).count = item.count;
                geometry.setAttribute('position', vertices);

                let normals = child.geometry.getAttribute('normal').clone();
                normals.array = normals.array.slice(item.start * normals.itemSize, (item.start + item.count) * normals.itemSize);
                (normals as any).count = item.count;
                geometry.setAttribute('normal', normals);

                geometry.computeBoundingBox();

                let center = new THREE.Vector3();
                geometry.boundingBox!.getCenter(center);
                let wNormal = (joinery.parent as Wall).normal;
                let wCenter = (joinery.parent as Wall).center;
                let plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
                    new THREE.Vector3(wNormal[0], wNormal[1], wNormal[2]),
                    new THREE.Vector3(wCenter[0] / 100, wCenter[1] / 100, 0)
                );
                let project = new THREE.Vector3();
                plane.projectPoint(center, project);
                let normal = new THREE.Vector3().subVectors(center, project).normalize();
                let Y = normal.clone();
                let Z = new THREE.Vector3(0, 0, 1);
                let X = new THREE.Vector3().crossVectors(Y, Z);
                let basis = new THREE.Matrix4().makeBasis(X, Y, Z).setPosition(center).invert();
                for (let k = 0; k < vertices.array.length; k += 3) {
                    let v = new THREE.Vector3(vertices.array[k], vertices.array[k + 1], vertices.array[k + 2]);
                    v.applyMatrix4(basis);
                    vertices.array[k] = v.x;
                    vertices.array[k + 1] = v.y;
                    vertices.array[k + 2] = v.z;
                }

                geometry.computeBoundingBox();
                let size = new THREE.Vector3();
                geometry.boundingBox!.getSize(size);
                geometry.dispose();
                geometry = new THREE.PlaneGeometry(size.x, size.z);
                let mirror = new Reflector(geometry, {
                    textureWidth: window.innerWidth,
                    textureHeight: window.innerHeight
                });
                mirror.name = child.name;
                let rotation = new THREE.Matrix4().makeRotationX(-Math.PI / 2);
                let mirrorBasis = new THREE.Matrix4().makeBasis(X, Y, Z).multiply(rotation);
                mirror.quaternion.setFromRotationMatrix(mirrorBasis);
                mirror.position.copy(center);
                mirror.position.addScaledVector(normal, size.y > 0.001 ? size.y / 2 + 0.001 : 0.001);
                this.mirrors.push({ object: mirror, entity: joinery });
                this.group.add(mirror);
            }

            return true;
        });
    }

    _handleJoinery(child: THREE.Mesh, entity: Entity) : void {
        if (child.name.indexOf("Joinery") === - 1) {
            return;
        }

        let joineryParse = child.name.split('_');
        let id = (child.name.startsWith("axo") ? Number(joineryParse[3]) : Number(joineryParse[2]));
        let joinery = this.savane.getDeepChild(id) as Joinery;
        if (!joinery) {
            return;
        }

        if (entity && entity.id !== id) {
            return;
        }

        if ((child as any).isGlass) return;

        let options = Material.joineryMaterialFromMaterialType(joinery.materialType);
        let coatings = joinery.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;
        coatings = coatings.filter((item) => item.hangType == Coating.HangType.joinery);
        if (options.coating) {
            // multimaterial coating
            Material.setMultiMaterial(child, options.coating, 1, this.scene, true, () => {
                if (Array.isArray(child.material)) {
                    this._createGlass(child, joinery);
                    this._createMirror(child, joinery, options);

                    if ((coatings !== null) && (coatings.length !== 0)) {
                        // Assign material to mesh
                        Material.setMaterialFromCoating(child, coatings[0], true, undefined, this.scene, false, null);
                    }
                }
                else {
                    if ((coatings !== null) && (coatings.length !== 0)) {
                        // Assign material to mesh
                        Material.setMaterialFromCoating(child, coatings[0], true, undefined, this.scene, false, null);
                    }
                }
                this._applyJoineryHoldout(joinery, child);
            });
        } else if ((coatings !== null) && (coatings.length !== 0)) {
            // Assign material to mesh
            Material.setMaterialFromCoating(child, coatings[0], true, undefined, this.scene, false, () => {
                this._applyJoineryHoldout(joinery, child);
            });
        } else {
            if (Array.isArray(child.material)) {
                let material = child.material as Array<THREE.MeshPhongMaterial>
                for (let i = 0; i < child.material.length; ++i) {
                    material[i].color = new THREE.Color(options.color).convertSRGBToLinear();
                    material[i].reflectivity = options.reflectivity;
                }

                this._createGlass(child, joinery);
                this._createMirror(child, joinery, options);
            } else {
                let material = child.material as THREE.MeshPhongMaterial
                material.color = new THREE.Color(options.color).convertSRGBToLinear();
                material.reflectivity = options.reflectivity;
            }
            this._applyJoineryHoldout(joinery, child);
        }
    }

    _applyJoineryHoldout(joinery: Joinery, child: THREE.Mesh) : void {
        if (joinery.holdout) {
            if (Array.isArray(child.material)) {
                for (let i = 0; i < child.material.length; ++i) {
                    let name = child.material[i].name;
                    if (name === "8" || name === "7") continue;
                    child.material[i] = holdoutMaterial.clone();
                    child.material[i].name = name;
                }
            } else {
                let name = child.material.name;
                child.material = holdoutMaterial.clone();
                child.material.name = name;
            }
        }
    }

    _handleWall(child: THREE.Mesh, entity: Entity) : void {
        if (!child.name.startsWith("Wall")) {
            return;
        }

        // add node to wall
        let wallParse = child.name.split('_');
        let wallId = Number(wallParse[1]);

        let hangType = Coating.HangType.wallDirect;
        if (wallParse[3] === 'Indirect') {
            hangType = Coating.HangType.wallUndirect;
        } else if (wallParse[2] === 'Top') {
            hangType = Coating.HangType.wallTop;
        } else if (wallParse[2] === 'Bottom') {
                hangType = Coating.HangType.wallBottom;
        } else if (wallParse[2] === 'RightSide') {
            hangType = Coating.HangType.wallRightSide;
        } else if (wallParse[2] === 'LeftSide') {
            hangType = Coating.HangType.wallLeftSide;
        }

        if (entity && entity.id !== wallId) {
            return;
        }

        let wall = this.savane.getDeepChild(wallId);
        if (!wall) return;

        if (wall.holdout) {
            if (Array.isArray(child.material)) {
                for (let i = 0; i < child.material.length; ++i) {
                    let name = child.material[i].name;
                    child.material[i] = holdoutMaterial.clone();
                    child.material[i].name = name;
                    (child as any).coatingId = undefined;
                }
            } else {
                let name = child.material.name;
                child.material = holdoutMaterial.clone();
                child.material.name = name;
                (child as any).coatingId = undefined;
            }
            return;
        }

        let coatings = wall.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;
        coatings = coatings.filter(function(item) {
            return item.hangType === hangType;
        });

        // Apply identified coating
        if ((coatings !== null) && (coatings.length > 0)) {
            for (let i = 0; i < coatings.length; i++) {
                if ((child as any).coatingId === coatings[i].coatingId) {
                    continue;
                }

                if (coatings[i].hangType === hangType) {
                    if (coatings[i].floorGeneratorSettings) {
                        if (coatings[i].floorGeneratorSettings.jointCoating) {
                            child.material = Material.default(new THREE.Color(coatings[i].floorGeneratorSettings.jointCoating.colors[0].realColor).convertSRGBToLinear(), null, {});
                            (child as any).coatingId = coatings[i].floorGeneratorSettings.jointCoating.coatingId;
                        } else {
                            child.material = Material.default(new THREE.Color(0xffffff).convertSRGBToLinear(), null, {});
                            (child as any).coatingId = undefined;
                        }
                    } else {
                        Material.setMaterialFromCoating(child, coatings[i], true, undefined, this.scene, false, null);
                        (child as any).coatingId = coatings[i].coatingId;
                    }
                    break;
                }
            }
        }
        else {
            //Set material
            child.material = Material.default(new THREE.Color(0x505050).convertSRGBToLinear(), null, {

            });
            (child as any).coatingId = undefined;
        }
    }

    _handleCeiling(child: THREE.Mesh, entity: Entity) {
        if (child.name.indexOf("Ceiling") === -1) {
            return;
        }

        //add node to wall
        let ceilingParse = child.name.split('_');
        let index = 1;
        if (child.name.startsWith("axo")) {
            index++;
        }
        let roomId = Math.abs(Number(ceilingParse[index]));
        let room = this.savane.getDeepChild(roomId) as Room;
        if (!room) {
            return;
        }

        if (entity && entity.id !== roomId) {
            return;
        }

        if (room.isPool) {
            child.material = Material.default(new THREE.Color(0x39AEE1), null, null);
            return;
        }

        let coatings = room.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;
        let found = false;

        if (coatings !== null) {
            // Assign material to mesh
            for (let i = 0; i < coatings.length; i++) {
                if (coatings[i].hangType === Coating.HangType.ceiling) {
                    Material.setMaterialFromCoating(child, coatings[i], true, undefined, this.scene, false, function() {
                        if (Array.isArray(child.material)) {
                            for (let i = 0; i < child.material.length; ++i) {
                                child.material[i].side = THREE.FrontSide;
                            }
                        } else {
                            child.material.side = THREE.FrontSide;
                        }
                    });
                    found = true;
                    (child as any).coatingId = coatings[i].coatingId;
                    break;
                }
            }
        }

        // Coating not found, assign default material
        if (!found) {
            // Default color assignment when no coating
            child.material = Material.default(new THREE.Color(0xffffff), null, null);
        }
    }

    _handleStair(child: THREE.Mesh, entity: Entity) {
        if (child.name.indexOf("Stair") === -1) {
            return;
        }

        let stairParse = child.name.split('_');
        let id = Number(stairParse[1]);
        if (child.name.startsWith("axo")) {
            id = Number(stairParse[2]);
        }
        let stair = this.savane.getDeepChild(id) as Staircase;
        if (!stair) {
            return;
        }

        if (entity && entity.id !== id) {
            return;
        }

        let stepColor = 0x542B17;
        let counterStepColor = 0xFFFFFF;
        let limonColor = 0x542B17;
        switch (stair.materialType) {
            case 0:
                counterStepColor = 0x542B17;
                break;

            case 1:
                counterStepColor = 0x542B17;
                break;

            case 2:
                break;

            case 3:
                break;

            case 4:
                counterStepColor = 0x542B17;
                limonColor = 0x0;
                break;

            case 5:
                stepColor = 0x0;
                break;

            case 6:
                stepColor = 0xCF9B6B;
                counterStepColor = 0xCF9B6B;
                limonColor = 0xCF9B6B;
                break;

            case 7:
                stepColor = 0xCF9B6B;
                counterStepColor = 0xCF9B6B;
                limonColor = 0xCF9B6B;
                break;

            case 8:
                stepColor = 0xCF9B6B;
                limonColor = 0xCF9B6B;
                break;

            case 9:
                stepColor = 0xCF9B6B;
                limonColor = 0xCF9B6B;
                break;

            case 10:
                stepColor = 0xCF9B6B;
                counterStepColor = 0xCF9B6B;
                limonColor = 0x0;
                break;

            case 11:
                stepColor = 0x0;
                limonColor = 0xCF9B6B;
                break;

            case 12:
                stepColor = 0x0;
                limonColor = 0x0;
                break;
            case 13:
                stepColor = 0xFFFFFF;
                limonColor = 0xFFFFFF;
                break;

            case 14:
                stepColor = 0xCF9B6B;
                counterStepColor = 0xCF9B6B;
                limonColor = 0xFFFFFF;
                break;
        }

        if (Array.isArray(child.material)) {
            for (let i = 0; i < child.material.length; ++i) {
                switch (child.material[i].name) {
                    case "1":
                        Cleaner.cleanMaterial(child.material[i]);
                        child.material[i] = Material.default(new THREE.Color(stepColor).convertSRGBToLinear(), null, { name: '1' });
                        break;

                    case "2":
                        Cleaner.cleanMaterial(child.material[i]);
                        child.material[i] = Material.default(new THREE.Color(counterStepColor).convertSRGBToLinear(), null, { name: '2' });
                        break;

                    case "3":
                        Cleaner.cleanMaterial(child.material[i]);
                        child.material[i] = Material.default(new THREE.Color(limonColor).convertSRGBToLinear(), null, { name: '3' });
                        break;
                }
            }
        }
        else {
            switch (child.material.name) {
                case "1":
                    Cleaner.cleanMaterial(child.material);
                    child.material = Material.default(new THREE.Color(stepColor).convertSRGBToLinear(), null, { name: '1' });
                    break;

                case "2":
                    Cleaner.cleanMaterial(child.material);
                    child.material = Material.default(new THREE.Color(counterStepColor).convertSRGBToLinear(), null, { name: '2' });
                    break;

                case "3":
                    Cleaner.cleanMaterial(child.material);
                    child.material = Material.default(new THREE.Color(limonColor).convertSRGBToLinear(), null, { name: '3' });
                    break;
            }
        }

        let coatings = stair.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;

        if ((coatings !== null) && (coatings.length !== 0)) {
            // Assign material to mesh
            for (let i = 0 ; i < coatings.length ; i++) {
                Material.setMaterialFromCoating(child, coatings[i], true, undefined, this.scene, false, null);
            }
        }
    }

    _handleStairGuardsRail(child: THREE.Mesh, entity: Entity) : void {
        if (!child.name.startsWith("StairGuardsRail")) {
            return;
        }

        let stairParse = child.name.split('_');
        let id = Number(stairParse[1]);
        let stair = this.savane.getDeepChild(id) as Staircase;
        if (!stair) {
            return;
        }

        if (entity && entity.id !== id) {
            return;
        }

        // Bois clair
        let material = {
            color: 0xCF9B6B,
            side: THREE.DoubleSide as number
        }
        switch (stair.rampMaterialType) {
            // Bois foncé
            case 1:
                material.color = 0x542B17;
                break;

            // Blanc
            case 2:
            // Chrome
            case 4:
            // Verre
            case 5:
                material.color = 0xffffff;
                break;

            // Noir
            case 3:
                material.color = 0x0;
                break;
        }

        material.side = (this.scene.settings.interactiveProject ? THREE.FrontSide : THREE.DoubleSide);
        let name = (child.material as THREE.MeshPhongMaterial).name;
        child.material = Material.default(new THREE.Color(material.color).convertSRGBToLinear(), null, material);
        child.material.name = name;

        switch (stair.rampMaterialType) {
            // Chrome
            case 4:
                (child.material as THREE.MeshPhongMaterial).reflectivity = 1.0;
                this._createCubeCamera(child, 256);
                break;

            // Verre
            case 5:
                child.material.transparent = true;
                child.material.opacity = 0.1;
                (child.material as THREE.MeshPhongMaterial).reflectivity = 0.15;
                child.castShadow = false;
                break;
        }

        let coatings = stair.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;

        if ((coatings !== null) && (coatings.length !== 0)) {
            // Assign material to mesh
            for (let i = 0 ; i < coatings.length ; i++) {
                Material.setMaterialFromCoating(child, coatings[i], true, undefined, this.scene, false, null);
            }
        }
    }

    _handlePlinth(child: THREE.Mesh, entity: Entity) : void {
        if (!child.name.startsWith("Plinth")) {
            return;
        }

        let plinthParse = child.name.split('_');
        let wallId = Number(plinthParse[1]);
        let directCoating = (plinthParse[3] === 'Direct');
        let roomId = Number(plinthParse[4]);
        let wall = this.savane.getDeepChild(wallId) as Wall;
        let room = this.savane.getDeepChild(roomId) as Room;
        if (!room) {
            return;
        }

        if (entity && entity.id !== roomId && entity.id !== wallId) {
            return;
        }

        // Handle Coating with wall plinth hang type
        if (wall !== null) {
            let coatings = wall.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;
            coatings = coatings.filter(function(item) {
                return item.hangType === Coating.HangType.plinthDirect || item.hangType === Coating.HangType.plinthUndirect;
            });
            if ((coatings !== null) && (coatings.length > 0)) {
                for (let i = 0; i < coatings.length; i++) {
                    if ((coatings[i].hangType === Coating.HangType.plinthDirect && directCoating) || (coatings[i].hangType === Coating.HangType.plinthUndirect && !directCoating)) {
                        Material.setMaterialFromCoating(child, coatings[i], true, undefined, this.scene, false, null);
                        (child as any).coatingId = coatings[i].coatingId;
                        return;
                    }
                }
            }
        }

        switch (room.plinthsMaterialType) {
            // White
            case 0:
                child.material = Material.default(new THREE.Color(0xffffff), null, {

                });
                break;

            // Like room floor
            case 1:
                let coatings = room.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;
                let hangType = Coating.HangType.floor;
                let found = false;
                for (let i = 0; i < coatings.length; i++) {

                    if (coatings[i].hangType === hangType) {
                        if (!coatings[i].floorGeneratorSettings) {
                            Material.setMaterialFromCoating(child, coatings[i], true, undefined, this.scene, false, null);
                            (child as any).coatingId = coatings[i].coatingId;
                        }
                        if (coatings[i].floorGeneratorSettings && coatings[i].floorGeneratorSettings.jointCoating) {
                            child.material = Material.default(new THREE.Color(coatings[i].floorGeneratorSettings.jointCoating.colors[0].realColor).convertSRGBToLinear(), null, {

                            });
                            (child as any).coatingId = coatings[i].floorGeneratorSettings.jointCoating.coatingId;
                        }
                        found = true;
                        break;
                    }
                }
                if (found == false) {
                    // Default color assignment when no coating
                    child.material = Material.default(new THREE.Color(0xffffff), null, {

                    });
                }
                break;

            // Like wall
            case 2:
                if (wall !== null) {
                    let coatings = wall.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;

                    if ((coatings !== null) && (coatings.length > 0)) {
                        for (let i = 0; i < coatings.length; i++) {
                            if ((child as any).coatingId === coatings[i].coatingId) {
                                continue;
                            }

                            if ((coatings[i].hangType === Coating.HangType.wallDirect && directCoating) || (coatings[i].hangType === Coating.HangType.wallUndirect && !directCoating)) {
                                Material.setMaterialFromCoating(child, coatings[i], true, undefined, this.scene, false, null);
                                (child as any).coatingId = coatings[i].coatingId;
                                break;
                            }
                        }
                    }
                    else {
                        //Set material
                        child.material = Material.default(new THREE.Color(0xffffff), null, {

                        });
                    }
                }
                break;
        }
    }

    _handleTechnicalElementPlinth(child: THREE.Mesh, entity: Entity) : void {
        if (!child.name.startsWith("TechnicalElementPlinth")) {
            return;
        }

        (child.material as THREE.MeshPhongMaterial).color = new THREE.Color(0xffffff);

        let plinthParse = child.name.split('_');
        let techElementId = Number(plinthParse[2]);
        let techElement = this.savane.getDeepChild(techElementId);
        if (!techElement) {
            return;
        }

        if (entity && entity.id !== techElementId) {
            return;
        }

        let coatings = techElement.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;
        if ((coatings !== null) && (coatings.length > 0)) {
            for (let i = 0 ; i < coatings.length ; i++) {
                if (coatings[i].hangType === Coating.HangType.plinthDirect) {
                    Material.setMaterialFromCoating(child, coatings[i], true, undefined, this.scene, false, null);
                    return;
                }
            }
        }

        let room = roomManager.getRoomAtPosition(techElement.position, techElement.floor!);
        if (!room) {
            return;
        }

        switch (room.plinthsMaterialType) {
            // White
            case 0:
                child.material = Material.default(new THREE.Color(0xffffff), null, { });
                break;

            // Like room floor
            case 1:
                {
                    let coatings = room.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;

                    if ((coatings !== null) && (coatings.length !== 0)) {
                        // Assign material to mesh
                        Material.setMaterialFromCoating(child, coatings[0], true, undefined, this.scene, false, null);
                    }
                    else {
                        // Default color assignment when no coating
                        child.material = Material.default(new THREE.Color(0xffffff), null, { });
                    }
                }
                break;

            // Like wall
            case 2:
                {
                    let coatings = techElement.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;

                    if ((coatings !== null) && (coatings.length > 0)) {
                        Material.setMaterialFromCoating(child, coatings[0], true, undefined, this.scene, false, null);
                    }
                    else {
                        // Default color assignment when no coating
                        child.material = Material.default(new THREE.Color(0xffffff), null, { });
                    }
                }
                break;
        }
    }

    _handleTechnicalElement(child: THREE.Mesh, entity: Entity) : void {
        let techElementParse = child.name.split('_');
        let index = techElementParse.indexOf("TechnicalElement");

        if (index === -1) {
            return;
        }

        let type = techElementParse[index + 1];
        let id = Number(techElementParse[index + 2]);
        let techElement = this.savane.getDeepChild(id) as TechnicalElement;
        let scene = this.scene;
        if (!techElement) {
            return;
        }

        if (techElement.holdout) {
            if (Array.isArray(child.material)) {
                for (let i = 0; i < child.material.length; ++i) {
                    let name = child.material[i].name;
                    child.material[i] = holdoutMaterial.clone();
                    child.material[i].name = name;
                }
            } else {
                let name = child.material.name;
                child.material = holdoutMaterial.clone();
                child.material.name = name;
            }
            return;
        }

        if (entity && entity.id !== id) {
            return;
        }

        let material = {
            color: 0xffffff,

        };

        let assignColor = true;

        switch (type) {
            case "Pole":
            case "Beam":
            case "Frame":
            if (techElement.isTechnicalElementEntity()) {
                let coatings = techElement.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;

                assignColor = false;

                if ((coatings !== null) && (coatings.length !== 0)) {
                    // Assign material to mesh
                    for(let i = 0 ; i < coatings.length ; i++) {
                        if (coatings[i].hangType === Coating.HangType.technicalElement || coatings[i].hangType === Coating.HangType.usemtl) {
                            Material.setMaterialFromCoating(child, coatings[i], true, undefined, this.scene, false, null);
                        }
                    }
                }
                else {

                    switch (techElement.materialType) {
                        // Neutre
                        case 0:
                            Material.setMaterialFromCoating(child, { coatingId: '5efde7003bf5f472eec2e35a' }, false, 1, this.scene, false, null);
                            break;

                        // Bois sombre
                        case 1:
                            Material.setMaterialFromCoating(child, { coatingId: '5efde656739226729d269a21' }, false, 1, this.scene, false, null);
                            break;

                        // Bois clair
                        case 2:
                            Material.setMaterialFromCoating(child, { coatingId: '5efde5fef2219b72a937d001' }, false, 1, this.scene, false, null);
                            break;

                        // Bois peint blanc
                        case 3:
                            Material.setMaterialFromCoating(child, { coatingId: '5efde5a31ca0ff72b4b0e2e6' }, false, 1, this.scene, false, null);
                            break;

                        // Bois usé
                        case 4:
                            Material.setMaterialFromCoating(child, { coatingId: '5efde53e9a372172a32f48a9' }, false, 1, this.scene, false, null);
                            break;

                        // Métal noir
                        case 5:
                            Material.setMaterialFromCoating(child, { coatingId: '5efde4dab25cfd72de0115c3' }, false, 1, this.scene, false, null);
                            break;

                        // Béton
                        case 6:
                            Material.setMaterialFromCoating(child, { coatingId: '5efde3efdf645372975fe02d' }, false, 1, this.scene, false, null);
                            break;

                        // Blanc
                        case 7:
                            Material.setMaterialFromCoating(child, { coatingId: '5efde47465cc93730671f88c' }, false, 1, this.scene, false, null);
                            break;

                        // Bois usé terne
                        case 8:
                            Material.setMaterialFromCoating(child, { coatingId: '60adfb4dbfbf5b3f21001d10' }, false, 1, this.scene, false, null);
                            break;

                        // Bois usé noeuds
                        case 9:
                            Material.setMaterialFromCoating(child, { coatingId: '60adfd59f961d43efd54b5bc' }, false, 1, this.scene, false, null);
                            break;

                        // Bois usé fissure
                        case 10:
                            Material.setMaterialFromCoating(child, { coatingId: '60adfe84d8bb9e3f324512a1' }, false, 1, this.scene, false, null);
                            break;

                        // Bois usé clair
                        case 11:
                            Material.setMaterialFromCoating(child, { coatingId: '60adff25fe46fa3ee5781ea6' }, false, 1, this.scene, false, null);
                            break;

                        // Bois brosse fonce
                        case 12:
                            Material.setMaterialFromCoating(child, { coatingId: '60ae0432dbafe33f0851f413' }, false, 1, this.scene, false, null);
                            break;

                        // Bois moyen
                        case 13:
                            Material.setMaterialFromCoating(child, { coatingId: '60af63ae96834c567a117d07' }, false, 1, this.scene, false, null);
                            break;

                        case 14:
                            Material.setMaterialFromCoating(child, { coatingId: '60ae0432dbafe33f0851f413' }, false, 1, this.scene, false, null);
                            break;

                        case 15:
                            Material.setMaterialFromCoating(child, { coatingId: '65e877a8d90439eef6cd8a5a' }, false, 1, this.scene, false, null);
                            break;
                    }
                }
            }
            else {
                let coatings = techElement.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;

                if ((coatings !== null) && (coatings.length > 0)) {
                    Material.setMaterialFromCoating(child, coatings[0], true, undefined, this.scene, false, null);
                    (child as any).coatingId = coatings[0].coatingId;
                    assignColor = false;
                } else {
                    // apply multi texture MTL
                    Material.setMaterialFromDecoratedWallsElement(child, techElement, this.scene, false, function() {
                        // remove glass for fix transparency problems
                        child.geometry.groups = child.geometry.groups.filter(function(item: any) {
                            if ((child.material as any)[item.materialIndex].transparent === true) {
                                (child.material as any)[item.materialIndex].alwaysTransparent = true;
                            }
                            return true;
                        });
                    });
                    assignColor = false;
                }
            }
            break;

            case "SpecialWall":
                {
                    let coatings = techElement.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;

                    if ((coatings !== null) && (coatings.length > 0)) {
                        for (let i = 0 ; i < coatings.length ; i++) {
                            Material.setMaterialFromCoating(child, coatings[i], true, undefined, this.scene, false, null);
                        }
                        assignColor = false;
                    } else {
                        // apply multi texture MTL
                        Material.setMaterialFromDecoratedWallsElement(child, techElement, this.scene, false, function() {
                            // remove glass for fix transparency problems
                            child.geometry.groups = child.geometry.groups.filter(function(item: any) {
                                if ((child.material as any)[item.materialIndex].transparent === true) {
                                    (child.material as any)[item.materialIndex].alwaysTransparent = true;
                                }
                                return true;
                            });
                        });
                        assignColor = false;
                    }
                }
                break;

            case "Radiator":
                {
                    let options = Material.radiatorMaterialFromMaterialType(techElement.materialType);
                    let coatings = techElement.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;

                    if ((coatings !== null) && (coatings.length !== 0)) {
                        // Assign material to mesh
                        Material.setMaterialFromCoating(child, coatings[0], true, undefined, this.scene, false, null);
                        assignColor = false;
                    } else {
                        // apply multi texture MTL
                        Material.setMultiMaterial(child, options.coating, 1, this.scene, true, () => {
                            if (techElement.materialType === 3) {
                                this._createCubeCamera(child, 256);
                                if (Array.isArray(child.material)) {
                                    let material = child.material as Array<THREE.MeshPhongMaterial>;
                                    for (let i = 0; i < material.length; ++i) {
                                        material[i].reflectivity = 1;
                                        material[i].envMap = (child as any).cubeCamera.renderTarget.texture;
                                    }
                                } else {
                                    let material = child.material as THREE.MeshPhongMaterial;
                                    material.reflectivity = 1;
                                    material.envMap = (child as any).cubeCamera.renderTarget.texture;
                                }
                            }
                        });
                        assignColor = false;
                    }
                }
                break;

            case "SpotLight":
                // Code désactivé car trop moche dans la 3D temps réel
                /*let intensity = 0;

                switch (techElement.intensity) {
                    case SceneConstants.SpotLightIntensity.lowPower:
                        intensity = 300;
                        break;

                    case SceneConstants.SpotLightIntensity.mediumPower:
                        intensity = 600;
                        break;

                    case SceneConstants.SpotLightIntensity.highPower:
                        intensity = 1000;
                        break;
                }

                if (intensity !== 0) {
                    // Extract light color with RGB model in a single color
                    let color = new THREE.Color(0xffffff);
                    let light = new THREE.SpotLight(color, intensity, 0, Math.PI / 2.1, 0.2, 2);
                    // Position the light itself
                    light.position.x = techElement.position[0] / 100;
                    light.position.y = techElement.position[1] / 100;
                    light.position.z = ((techElement.position[2] - (techElement.height / 2)) / 100);

                    // Create the light target
                    let target = new THREE.Object3D();
                    target.position.x = light.position.x;
                    target.position.y = light.position.y;
                    target.position.z = light.position.z - 10.0;
                    light.target = target;

                    // Temporary light helper to remove
                    // let lightHelper   = new THREE.CameraHelper(light.shadow.camera);
                    // END

                    // This object creates shadows - disable otherwise it can crash
                    light.castShadow = false;

                    this.group.add(light.target);
                    // Temporary light helper to remove
                    // this.group.add(lightHelper);
                    // END
                    this.group.add(light);
                }*/
                break;

            case "Rosette":
            case "CeilingBox":
                {
                    let coatings = techElement.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;

                    if ((coatings !== null) && (coatings.length !== 0)) {
                        // Assign material to mesh
                        for (let i = 0 ; i < coatings.length ; i++) {
                            Material.setMaterialFromCoating(child, coatings[i], true, undefined, scene, false, null);
                        }
                        assignColor = false;
                    }
                }
                break;

            case "WallDecoration":
            case "Fireplace":
            case "Stove":
            case "Boiler":
            case "SwitchBoard":
            case "AirConditioner":
                Material.setMaterialFromTechnicalElement(child, techElement, scene, false, function() {
                    // remove glass for fix transparency problems
                    child.geometry.groups = child.geometry.groups.filter(function(item: any) {
                        if ((child.material as any)[item.materialIndex].transparent === true) {
                            (child.material as any)[item.materialIndex].alwaysTransparent = true;
                        }
                        if ((child.material as any)[item.materialIndex].opacity === 0) {
                            return false;
                        }
                        return true;
                    });

                    let coatings = techElement.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;

                    for (let i = 0 ; i < coatings.length ; i++) {
                        Material.setMaterialFromCoating(child, coatings[i], true, undefined, scene, false, null);
                    }
                });
                assignColor = false;
                break;

            case "GuardRail":
                Material.setMaterialFromDecoratedWallsElement(child, techElement, scene, false, function() {
                    // remove glass for fix transparency problems
                    child.geometry.groups = child.geometry.groups.filter(function(item: any) {
                        if ((child.material as any)[item.materialIndex].transparent === true) {
                            (child.material as any)[item.materialIndex].alwaysTransparent = true;
                        }
                        if ((child.material as any)[item.materialIndex].opacity === 0) {
                            return false;
                        }
                        return true;
                    });

                    let coatings = techElement.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;

                    for (let i = 0 ; i < coatings.length ; i++) {
                        Material.setMaterialFromCoating(child, coatings[i], true, undefined, scene, false, null);
                    }
                });
                assignColor = false;
                break;
        }

        if (assignColor) {
            if (Array.isArray(child.material)) {
                for (let i = 0; i < child.material.length; ++i) {
                    let name = child.material[i].name;
                    child.material[i] = Material.default(new THREE.Color(material.color).convertSRGBToLinear(), null, material);
                    child.material[i].name = name;
                }
            } else {
                let name = child.material.name;
                child.material = Material.default(new THREE.Color(material.color).convertSRGBToLinear(), null, material);
                child.material.name = name;
            }
        }
    }

    _handlePoolEdge(child: THREE.Mesh, entity: Entity) : void {
        if (child.name.indexOf("PoolEdge") === -1) {
            return;
        }

        //add node to wall
        let PoolEdgeParse = child.name.split('_');
        let roomId = Math.abs(Number(PoolEdgeParse[1]));
        let room = this.savane.getDeepChild(roomId);
        if (!room) {
            return;
        }

        if (entity && entity.id !== roomId) {
            return;
        }

        let coatings = room.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;
        if (coatings !== null) {
            // Assign material to mesh
            for (let i = 0; i < coatings.length; i++) {
                if (coatings[i].hangType === Coating.HangType.ceiling) {
                    Material.setMaterialFromCoating(child, coatings[i], true, undefined, this.scene, false, function() {
                        if (Array.isArray(child.material)) {
                            for (let i = 0; i < child.material.length; ++i) {
                                child.material[i].side = THREE.FrontSide;
                            }
                        } else {
                            child.material.side = THREE.FrontSide;
                        }
                    });
                    (child as any).coatingId = coatings[i].coatingId;
                    break;
                }
            }
        }
    }

    _setTransparencyCallback(child: THREE.Mesh) : void {
        let self = this;
        child.onBeforeRender = function(renderer, scene, camera, geometry, material, group) {
            if ((material as any).alwaysTransparent) {
                return;
            }
            if (self.scene.hullTransparency === false) {
                if (!(material as any).isGlass) {
                    material.transparent = false;
                } else {
                    material.opacity = 0.05;
                }
                material.needsUpdate = true;
                return;
            }
            if (camera.parent && camera.parent.type === "CubeCamera") return;

            let direction = new THREE.Vector3();
            camera.getWorldDirection(direction);
            let target = (self.scene.defaultCamera as any).target;
            for (let i = 0; i < self.scene.planCameras.length; ++i) {
                if (camera === self.scene.planCameras[i].object) {
                    target = (self.scene.planCameras[i] as any).target;
                }
            }
            if (!target) return;
            let distanceToTarget = new THREE.Vector3().subVectors(target, camera.position).length();
            let center = new THREE.Vector3();
            if (!geometry.boundingBox) {
                geometry.computeBoundingBox();
            }
            geometry.boundingBox!.getCenter(center);
            let distanceToCenter = new THREE.Vector3().subVectors(center, camera.position).length();
            if (distanceToTarget > distanceToCenter) {
                material.transparent = true;
                material.opacity = 0.05;
            } else {
                if (!(material as any).isGlass) {
                    material.transparent = false;
                } else {
                    material.opacity = 0.05;
                }
            }
            material.needsUpdate = true;
        };
    }

    _processChild(child: any) {
        if (!this._entity) {
            if (child.isMesh) {
                let euler = new THREE.Euler(Math.PI * 0.5, 0, 0, 'XYZ');
                child.geometry.applyMatrix4(new THREE.Matrix4().makeRotationFromEuler(euler));
                child.geometry.applyMatrix4(new THREE.Matrix4().makeScale(0.01, 0.01, 0.01));
            }

            //hull creation - link elements to scene entities
            let entity = this.getEntityFromChild(child);
            if (entity) {
                if (this.scene.interactionWithItemAllowed(entity)) {
                    this.scene.planEntities.push(new WebglHullEntity(entity, this.scene, child, this, false, false));
                } else if (this.scene.itemSelectionAllowed(entity)) {
                    this.scene.planEntities.push(new WebglHullEntity(entity, this.scene, child, this, false, true));
                } else {
                    this.scene.nonInteractivePlanEntities.push(new WebglHullEntity(entity, this.scene, child, this, false, true));
                }
            }
            //Enable shadow
            child.receiveShadow = true;
            child.castShadow = true;
        }

        this._handleAxo(child, this._entity!);
        this._handleJoinery(child, this._entity!);
        this._handleWall(child, this._entity!);
        this._handleCeiling(child, this._entity!);
        this._handleStair(child, this._entity!);
        this._handleStairGuardsRail(child, this._entity!);
        this._handlePlinth(child, this._entity!);
        this._handleTechnicalElementPlinth(child, this._entity!);
        this._handleTechnicalElement(child, this._entity!);
        this._handlePoolEdge(child, this._entity!);

        if (!this._entity) {
            if (child.isMesh) {
                this._setTransparencyCallback(child);
            }
        }

        if (!child.material && child.isMesh) {
            //child.material = Material.default(new THREE.Color(0xffffff), null, {});
        }

        // apply customCoating
        for (let i = 0; i < this.savane.children.length; ++i) {
            let floor = this.savane.children[i] as Floor;
            if (floor.entityType === SceneConstants.EntityType.Floor) {
                let name = child.name.replace(/_[a-fA-F0-9]{24}_\d+/, '');
                let custom = floor.getCustomCoating(name);
                if (custom) {
                    Material.setMaterialFromCoating(child, custom.coating, true, undefined, this.scene, false, null);
                    (child as any).coatingId = custom.coating.coatingId;
                }
            }
        }

        // set envMap to material
        if ((child as any).cubeCamera) {
            if (Array.isArray(child.material)) {
                for (let i = 0; i < child.material.length; ++i) {
                    (child.material[i] as THREE.MeshPhongMaterial).envMap = (child as any).cubeCamera.renderTarget.texture;
                }
            } else {
                (child.material as THREE.MeshPhongMaterial).envMap = (child as any).cubeCamera.renderTarget.texture;
            }
        }
    }

}
